import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Flag, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { BulaLogo } from "@/components/BulaLogo";

export default function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await forgotPassword(email);
    setLoading(false);
    if (r.ok) {
      setSent(true);
      toast.success("Check your email for a reset link");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F8F7F3]">
      <div className="relative hidden md:block">
        <img src="https://images.unsplash.com/photo-1535131749006-b7f58c99034b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F2537]/90 to-[#0F2537]/40" />
        <div className="relative z-10 h-full p-12 flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2 w-fit" data-testid="forgot-logo">
            <BulaLogo size={36} />
            <span className="font-heading font-black text-xl">Bula Golf</span>
          </Link>
          <div>
            <div className="label-eyebrow text-white/60 mb-2">Forgot password</div>
            <h2 className="font-heading font-black text-4xl leading-tight">Let's get you back on the course.</h2>
            <p className="text-white/70 mt-3 max-w-sm text-sm leading-relaxed">A reset link will land in your inbox within seconds — valid for one hour, single use only.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        {sent ? (
          <div className="w-full max-w-md card-default p-8 rise text-center" data-testid="forgot-sent-card">
            <div className="w-14 h-14 rounded-full bg-[#E05D3A]/10 flex items-center justify-center mx-auto">
              <CheckCircle size={32} weight="fill" className="text-[#E05D3A]" />
            </div>
            <h1 className="font-heading font-black text-3xl mt-4">Check your inbox</h1>
            <p className="text-[#0F2537]/70 text-sm mt-2 leading-relaxed">
              If <strong>{email}</strong> is registered, we just sent a reset link.
              The email may take up to a minute to arrive — also check your spam folder.
            </p>
            <Link to="/login" data-testid="forgot-back-to-login" className="inline-block mt-6 px-5 py-3 rounded-xl bg-[#0F2537] text-white font-bold hover:bg-[#0F2537]/90 transition">
              Back to sign in
            </Link>
            <p className="text-xs text-[#0F2537]/50 mt-4">
              Didn't get an email? Try again with a different address.
            </p>
            <button onClick={() => { setSent(false); setEmail(""); }} data-testid="forgot-try-another"
              className="mt-2 text-xs font-semibold text-[#E05D3A] underline">
              Use a different email
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md card-default p-8 rise" data-testid="forgot-form">
            <span className="label-eyebrow">Forgot password</span>
            <h1 className="font-heading font-black text-3xl mt-1">Reset your password</h1>
            <p className="text-[#0F2537]/70 text-sm mt-1">Enter the email you signed up with — we'll send a reset link.</p>

            <label className="block mt-6 text-sm font-semibold">Email</label>
            <div className="mt-1 relative">
              <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0F2537]/40" />
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email-input" placeholder="you@example.com"
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]" />
            </div>

            <button type="submit" disabled={loading} data-testid="forgot-submit-btn"
              className="mt-6 w-full py-3 rounded-xl bg-[#0F2537] text-white font-bold hover:bg-[#0F2537]/90 transition disabled:opacity-60">
              {loading ? "Sending…" : "Send reset link"}
            </button>

            <p className="mt-5 text-sm text-[#0F2537]/70 text-center">
              Remembered it?{" "}
              <Link to="/login" data-testid="forgot-to-login-link" className="text-[#E05D3A] font-semibold">Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
